// Multi-language support for Arabic, Turkish, and English
export type Language = 'en' | 'tr' | 'ar';

export const languages: Record<Language, string> = {
  en: 'English',
  tr: 'Türkçe', 
  ar: 'العربية'
};

export const translations = {
  en: {
    nav: {
      home: 'Home',
      cars: 'Our Fleet',
      services: 'Services',
      campaigns: 'Campaigns',
      about: 'About Us',
      contact: 'Contact',
      dashboard: 'Dashboard'
    },
    hero: {
      title: 'Premium Car Rental in Turkey',
      subtitle: 'Discover Turkey with our reliable and comfortable vehicles. Professional service guaranteed.',
      ctaRent: 'Rent Now',
      ctaContact: 'Contact Us',
      whatsapp: 'WhatsApp',
      phone: 'Phone',
      driverService: 'Driver Service'
    },
    featuredCars: {
      title: 'Our Featured Cars',
      subtitle: 'Explore our selection of premium vehicles available for rent',
      viewAll: 'View All Cars'
    },
    booking: {
      title: 'Find Your Perfect Car',
      pickupLocation: 'Pickup Location',
      dropoffLocation: 'Dropoff Location', 
      pickupDate: 'Pickup Date',
      dropoffDate: 'Dropoff Date',
      searchCars: 'Search Cars',
      selectLocation: 'Select location',
      pickupPlaceholder: 'Istanbul Airport',
      dropoffPlaceholder: 'Sultanahmet',
      pickDatePlaceholder: 'Pick a date',
      locations: [
        { value: 'ist-airport', label: 'Istanbul Airport (IST)' },
        { value: 'saw-airport', label: 'Istanbul Sabiha Gökçen Airport (SAW)' },
        { value: 'sisli', label: 'Istanbul Şişli (European side)' },
        { value: 'fatih', label: 'Istanbul Fatih (European side)' },
        { value: 'levet', label: 'Istanbul Levet (European side)' },
        { value: 'sultanahmed', label: 'Istanbul Sultan Ahmed (European side)' },
        { value: 'kadikoy', label: 'Istanbul Kadıköy (European side)' },
        { value: 'viaport', label: 'Istanbul Viaport (Anatolian side)' }
      ],
      bookCar: 'Book',
      fillForm: 'Please fill in your details to complete the booking',
      personalInfo: 'Personal Information',
      fullName: 'Full Name',
      fullNamePlaceholder: 'John Doe',
      phone: 'Phone Number',
      email: 'Email',
      birthDate: 'Birth Date',
      birthPlace: 'Birth Place',
      birthPlacePlaceholder: 'City, Country',
      tcIdentityNumber: 'T.C. Identity Number',
      tcIdentityNumberPlaceholder: '12345678901',
      passportNumber: 'Passport Number',
      passportNumberPlaceholder: 'AB1234567',
      passportIssuedDate: 'Passport Issued Date',
      passportIssuedBy: 'Passport Issued By',
      passportIssuedByPlaceholder: 'Country/Authority',
      licenseNumber: 'License Number',
      licenseNumberPlaceholder: 'DL123456',
      licenseIssuedDate: 'License Issued Date',
      licenseIssuedBy: 'License Issued By',
      licenseIssuedByPlaceholder: 'Country/Authority',
      localAddress: 'Local Address',
      localAddressPlaceholder: 'Full address',
      localPhone: 'Local Phone',
      businessAddress: 'Business Address',
      businessAddressPlaceholder: 'Business address',
      businessPhone: 'Business Phone',
      invoiceAddress: 'Invoice Address',
      invoiceAddressPlaceholder: 'Invoice address',
      taxOffice: 'Tax Office',
      taxOfficePlaceholder: 'Tax office name',
      taxNumber: 'Tax Number',
      taxNumberPlaceholder: 'Tax number',
      selectDate: 'Select date',
      rentalDetails: 'Rental Details',
      additionalInfo: 'Additional Information',
      secondDriverName: 'Second Driver Name',
      secondDriverLicense: 'Second Driver License',
      secondDriverPassport: 'Second Driver Passport',
      secondDriverNamePlaceholder: 'Full name',
      secondDriverLicensePlaceholder: 'License number',
      secondDriverPassportPlaceholder: 'Passport number',
      paymentAmount1: 'Payment Amount 1',
      paymentAmount2: 'Payment Amount 2',
      sameAsPickup: 'Same as pickup',
      pickupTime: 'Pickup Time',
      dropoffTime: 'Dropoff Time',
      flightCode: 'Flight Code',
      optional: 'optional',
      withDriver: 'With Driver',
      rentalPeriod: 'Rental Period',
      days: 'days',
      pricePerDay: 'Price per Day',
      totalPrice: 'Total Price',
      cancel: 'Cancel',
      confirmBooking: 'Confirm Booking',
      submitting: 'Submitting...',
      fillRequired: 'Please fill in all required fields',
      invalidDates: 'Dropoff date must be after pickup date',
      success: 'Booking submitted successfully! We will contact you soon.',
      error: 'Failed to create booking. Please try again.',
      searching: 'Searching for available cars...'
    },
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive car rental services designed for your comfort and convenience in Turkey',
      subtitleShort: 'Professional car rental services tailored for your comfort and convenience in Turkey',
      viewAllServices: 'View All Services',
      premiumServices: 'Premium Services',
      professionalDrivers: 'Professional Drivers',
      available247: '24/7 Available',
      insuranceIncluded: 'Insurance Included',
      allIstanbulAirports: 'All Istanbul Airports',
      flightTracking: 'Flight Tracking',
      meetGreet: 'Meet & Greet',
      hotelDelivery: 'Hotel Delivery',
      cityCenter: 'City Center',
      freeService: 'Free Service',
      learnMore: 'Learn More',
      fullyInsuredTitle: 'Fully Insured',
      completeCoverage: 'Complete coverage',
      support247Title: '24/7 Support',
      alwaysAvailable: 'Always available',
      premiumFleet: 'Premium Fleet',
      latestModels: 'Latest models',
      fiveStarServiceTitle: '5 Star Service',
      customerRated: 'Customer rated',
      additionalServices: 'Additional Services',
      whyChoose: 'Why Choose Zeyn Tour?',
      getQuote: 'Get Quote',
      addToBooking: 'Add to Booking',
      readyToBook: 'Ready to Book?',
      contactNow: 'Contact us now to discuss your requirements and get a personalized quote for your car rental needs.',
      whatsappUs: 'WhatsApp Us',
      callNow: 'Call Now',
      withDriver: 'Car with Driver',
      withDriverDesc: 'Professional driver service for your comfort and safety',
      airportPickup: 'Airport Transfer',
      airportPickupDesc: 'Direct pickup service from Istanbul airports',
      delivery: 'Car Delivery',
      deliveryDesc: 'We deliver your rental car to any location in Istanbul',
      fullyInsured: 'Fully Insured',
      fullyInsuredDesc: 'Comprehensive insurance coverage on all vehicles',
      support247: '24/7 Support',
      support247Desc: 'Round-the-clock customer service and roadside assistance',
      fiveStarService: '5 Star Service',
      fiveStarServiceDesc: 'Rated excellent by thousands of satisfied customers',
      nationwideCoverage: 'Nationwide Coverage',
      nationwideCoverageDesc: 'Service available across all major Turkish cities',
      perDay: 'per day',
      featuresLabel: 'Features'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch with us for premium car rental services across Turkey. We\'re here to help 24/7.',
      sendMessage: 'Send us a Message',
      formDescription: 'Fill out the form below and we\'ll get back to you within 24 hours',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone Number',
      service: 'Service Needed',
      location: 'Preferred Location',
      message: 'Message',
      sending: 'Sending...',
      sendMsg: 'Send Message',
      quickContact: 'Quick Contact',
      quickContactDesc: 'Need immediate assistance? Contact us directly',
      businessHours: 'Business Hours',
      followUs: 'Follow Us',
      website: 'Website',
      workingHours: 'Working Hours',
      emergencySupport: 'Emergency Support',
      alwaysAvailable: 'Always Available',
      operateDesc: 'We operate 24/7 to ensure you always have access to our services when you need them.',
      serviceLocations: 'Our Service Locations',
      serviceAreas: 'Service Areas',
      serviceAreasDesc: 'Istanbul, Ankara, Antalya, Izmir and all major Turkish cities. Airport pickup and city delivery available.',
      mondaySunday: 'Monday - Sunday',
      available247: '24/7 Available',
      generalInquiries: 'General Inquiries',
      reservations: 'Reservations',
      driverService: 'Driver Service',
      whatsapp: 'WhatsApp',
      coverageAreas: 'Coverage Areas',
      selectService: 'Select a service',
      selectLocation: 'Select location',
      carRental: 'Car Rental',
      withDriver: 'Car with Driver',
      airportTransfer: 'Airport Transfer',
      longTerm: 'Long-term Rental',
      other: 'Other',
      istanbul: 'Istanbul',
      ankara: 'Ankara',
      antalya: 'Antalya',
      izmir: 'Izmir',
      otherCity: 'Other City',
      contactMethod: {
        generalInq: 'General Inquiries',
        reservations: 'Reservations',
        driver: 'Driver Service',
        driverservice: 'Driver Service',
        whatsapp: 'WhatsApp',
        callDesc: 'Call us for immediate assistance',
        bookDesc: 'Book your car rental',
        chauffeurDesc: 'Chauffeur and driver services',
        chatDesc: 'Chat with us instantly'
      },
      locations: {
        istanbul: 'Istanbul',
        istanbulDesc: 'Main office and largest fleet',
        ankara: 'Ankara',
        ankaraDesc: 'Capital city service center',
        antalya: 'Antalya',
        antalyaDesc: 'Tourism hub operations',
        izmir: 'Izmir',
        izmirDesc: 'Aegean region coverage'
      },
      form: {
        nameLabel: 'Full Name',
        emailLabel: 'Email',
        phoneLabel: 'Phone Number',
        servicePlaceholder: 'Select a service',
        locationPlaceholder: 'Select location',
        messagePlaceholder: 'Tell us about your rental needs, dates, preferences...'
      }
    },
    about: {
      badge: 'Est. 2015',
      title: 'Welcome to Zeyn Tour',
      subtitle: 'Where Every Journey Begins with a Smile (and a Really Nice Car)',
      intro: 'We\'re not just another car rental company. We\'re the friends you call when you need a ride, the folks who remember how you like your coffee, and yes, the people who obsess over keeping our cars spotlessly clean.',
      happyCustomers: 'Happy Customers',
      premiumCars: 'Premium Cars',
      citiesCovered: 'Cities Covered',
      coffeeDrunk: 'Coffee Consumed',
      ourStory: 'Our Story',
      storyTagline: 'Or: How We Turned a Passion for Cars into Your Perfect Ride',
      storyPara1: 'It all started in 2015 with two friends, three cars, and an unreasonable amount of optimism. We believed that renting a car shouldn\'t feel like a chore – it should feel like the beginning of an adventure.',
      storyPara2: 'Fast forward to today: we\'ve helped over 10,000 customers explore Turkey in style. Some came for business, others for pleasure, and a few just wanted to see if our cars were as nice as we said (spoiler: they are).',
      storyPara3: 'Our secret? We treat every car like it\'s ours, and every customer like family. Well, the cool kind of family that brings you snacks and never asks awkward questions at dinner.',
      meetTeam: 'Meet the Team',
      teamTagline: 'The Humans Behind the Wheels',
      ceo: 'The Visionaries',
      ceoDesc: 'Our founders still can\'t believe people trust us with their vacation plans. We promise we\'re professionals (most of the time).',
      drivers: 'Our Drivers',
      driversDesc: 'Part GPS, part tour guide, part comedian. They know every shortcut, every good restaurant, and every bad joke in Turkey.',
      support: 'Customer Support Heroes',
      supportDesc: '24/7 availability means they\'ve answered questions at 3 AM in their pajamas more times than they\'d like to admit.',
      mechanics: 'The Car Whisperers',
      mechanicsDesc: 'They can hear a problem before it happens. Seriously, it\'s almost magical. Or maybe they just check the cars a lot. Probably that.',
      ourValues: 'What We Stand For',
      valuesTagline: 'Besides Really Clean Cars',
      value1Title: 'Honesty',
      value1Desc: 'No hidden fees, no surprises. Except maybe finding an upgrade because we like you.',
      value2Title: 'Joy',
      value2Desc: 'Life\'s too short for boring car rentals. We bring the fun (and the comfortable seats).',
      value3Title: 'Excellence',
      value3Desc: 'We\'re perfectionists. Ask our detail team who spends 2 hours per car making it sparkle.'
    },
    cars: {
      title: 'Our Premium Fleet',
      subtitle: 'Choose from our premium fleet of vehicles for your perfect journey in Turkey',
      filterCars: 'Filter Cars',
      searchCars: 'Search cars...',
      brand: 'Brand',
      allBrands: 'All Brands',
      transmission: 'Transmission',
      allTransmissions: 'All Transmissions',
      automatic: 'Automatic',
      manual: 'Manual',
      fuelType: 'Fuel Type',
      allFuelTypes: 'All Fuel Types',
      petrol: 'Petrol',
      diesel: 'Diesel',
      hybrid: 'Hybrid',
      electric: 'Electric',
      seats: 'Seats',
      anySeats: 'Any Seats',
      luggage: 'Luggage',
      luggageCapacity: 'Suitcases',
      minLicenseAge: 'Min. License Age',
      years: 'years',
      priceRange: 'Price Range',
      anyPrice: 'Any Price',
      available: 'Available',
      bookNow: 'Book Now',
      viewDetails: 'View Details',
      perDay: 'per day',
      showing: 'Showing',
      of: 'of',
      vehicles: 'vehicles',
      noCarsFound: 'No cars found',
      adjustFilters: 'Try adjusting your filters to see more results',
      features: 'Features',
      moreFeatures: 'more',
      year: 'Year'
    },
    carDetails: {
      backToCars: 'Back to Cars',
      unavailable: 'Unavailable',
      specifications: 'Specifications',
      people: 'people',
      location: 'Location',
      brand: 'Brand',
      features: 'Features',
      pricing: 'Pricing',
      dailyRate: 'Daily Rate',
      weeklyRate: 'Weekly Rate',
      monthlyRate: 'Monthly Rate',
      yearlyRate: 'Yearly Rate',
      contactWhatsApp: 'Contact via WhatsApp',
      bookNow: 'Book Now'
    },
    carFeatures: {
      'manual transmission': 'Manual Transmission',
      'automatic transmission': 'Automatic Transmission',
      'economic': 'Economic',
      'air conditioning': 'Air Conditioning',
      'bluetooth': 'Bluetooth',
      'gps navigation': 'GPS Navigation',
      'usb charging': 'USB Charging',
      'backup camera': 'Backup Camera',
      'cruise control': 'Cruise Control',
      'sunroof': 'Sunroof',
      'leather seats': 'Leather Seats',
      'heated seats': 'Heated Seats',
      'premium audio': 'Premium Audio',
      'digital dashboard': 'Digital Dashboard',
      'advanced safety': 'Advanced Safety',
      'hybrid engine': 'Hybrid Engine',
      'all weather': 'All Weather',
      'high seating': 'High Seating',
      'suv design': 'SUV Design',
      'compact design': 'Compact Design',
      'spacious interior': 'Spacious Interior',
      'fuel efficient': 'Fuel Efficient',
      'modern tech': 'Modern Tech'
    },
    footer: {
      description: 'Premium car rental services in Turkey. Professional drivers, luxury vehicles, and exceptional service for all your transportation needs.',
      quickLinks: 'Quick Links',
      home: 'Home',
      ourFleet: 'Our Fleet',
      services: 'Services',
      campaigns: 'Campaigns',
      contact: 'Contact',
      deliveryForm: 'Delivery Form',
      approvedEcommerce: 'Approved E-Commerce Assurance',
      contactInfo: 'Contact Info',
      serviceInCities: 'Service in all major cities',
      copyright: '© 2024 Zeyn Tour. All rights reserved.',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      admin: 'Admin',
      poweredBy: 'Powered by Prebitit'
    },
    campaigns: {
      title: 'Special Campaigns',
      subtitle: 'Discover our exclusive offers and special deals for your car rental needs',
      bookNow: 'Book Now',
      backToCampaigns: 'Back to Campaigns',
      forRentals: 'FOR RENTALS OF 3 DAYS OR MORE',
      startingFrom: 'STARTING FROM'
    },
    dashboard: {
      title: 'Admin Dashboard',
      tabs: {
        cars: 'Manage Cars',
        bookings: 'Manage Bookings',
        services: 'Manage Services',
        campaigns: 'Manage Campaigns',
        messages: 'Contact Messages',
        analytics: 'Analytics'
      },
      loading: 'Loading',
      manage: {
        addNew: 'Add New',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        update: 'Update',
        create: 'Create',
        actions: 'Actions',
        confirmDelete: 'Are you sure you want to delete this item?',
        uploading: 'Uploading...',
        noDataFound: 'No data found'
      },
      bookingsMgmt: {
        title: 'Manage Bookings',
        addNew: 'Add New Booking',
        createNew: 'Create New Booking',
        customerName: 'Customer Name',
        car: 'Car',
        customerEmail: 'Email',
        customerPhone: 'Phone',
        pickupLocation: 'Pickup Location',
        dropoffLocation: 'Dropoff Location',
        pickupDate: 'Pickup Date',
        dropoffDate: 'Dropoff Date',
        totalPrice: 'Total Price',
        status: 'Status',
        withDriver: 'With Driver',
        notes: 'Notes',
        days: 'Days',
        total: 'Total',
        driver: 'Driver',
        contact: 'Contact',
        pickup: 'Pickup',
        dropoff: 'Dropoff',
        dates: 'Dates',
        sameAsPickup: 'Same as pickup',
        pending: 'Pending',
        confirmed: 'Confirmed',
        cancelled: 'Cancelled',
        yes: 'Yes',
        no: 'No'
      },
      carsMgmt: {
        title: 'Manage Cars',
        addNew: 'Add New Car',
        editCar: 'Edit Car',
        carImages: 'Car Images',
        uploadImages: 'Click to upload images',
        imageFormat: '(JPG, PNG, WEBP up to 5MB)',
        youtubeLink: 'YouTube Video Link (Optional)',
        brand: 'Brand',
        model: 'Model',
        year: 'Year',
        seats: 'Seats',
        luggage: 'Luggage Capacity',
        luggageCapacity: 'Suitcases',
        minLicenseAge: 'Min. License Age',
        transmission: 'Transmission',
        automatic: 'Automatic',
        manual: 'Manual',
        fuelType: 'Fuel Type',
        petrol: 'Petrol',
        diesel: 'Diesel',
        petrolDiesel: 'Petrol/Diesel',
        hybrid: 'Hybrid',
        electric: 'Electric',
        dailyPrice: 'Daily Price',
        weeklyPrice: 'Weekly Price',
        monthlyPrice: 'Monthly Price',
        yearlyPrice: 'Yearly Price',
        optional: 'Optional',
        location: 'Location',
        availability: 'Availability',
        available: 'Available',
        notAvailable: 'Not Available',
        image: 'Image',
        brandModel: 'Brand & Model',
        pricePerDay: 'Price/Day',
        actions: 'Actions',
        noImage: 'No Image',
        addCar: 'Add Car',
        updateCar: 'Update Car',
        vehiclePlate: 'Vehicle Plate',
        vehicleGroup: 'Vehicle Group'
      },
      servicesMgmt: {
        title: 'Manage Services',
        addNew: 'Add New Service',
        editService: 'Edit Service',
        nameEn: 'Name (English)',
        nameTr: 'Name (Turkish)',
        nameAr: 'Name (Arabic)',
        descriptionEn: 'Description (English)',
        descriptionTr: 'Description (Turkish)',
        descriptionAr: 'Description (Arabic)',
        price: 'Price',
        priceType: 'Price Type',
        perDay: 'Per Day',
        perWeek: 'Per Week',
        perMonth: 'Per Month',
        fixed: 'Fixed',
        active: 'Active',
        inactive: 'Inactive',
        name: 'Name',
        description: 'Description',
        status: 'Status',
        image: 'Image'
      },
      analyticsMgmt: {
        title: 'Analytics Overview',
        totalCars: 'Total Cars',
        activeCars: 'Active Cars',
        totalBookings: 'Total Bookings',
        activeBookings: 'Active Bookings',
        totalRevenue: 'Total Revenue',
        thisMonth: 'This Month',
        avgBookingValue: 'Avg. Booking Value',
        perBooking: 'per booking',
        fleetUtilization: 'Fleet Utilization',
        carsInUse: 'Cars in use',
        bookingRate: 'Booking Rate',
        bookingsPerCar: 'Bookings per car',
        quickInsights: 'Quick Insights',
        mostPopularService: 'Most Popular Service',
        carRentalWithDriver: 'Car rental with driver',
        peakSeason: 'Peak Season',
        summerMonths: 'Summer months (Jun-Aug)',
        trending: 'Trending',
        seasonal: 'Seasonal'
      },
      campaignsMgmt: {
        title: 'Manage Campaigns',
        addNew: 'Add New Campaign',
        editCampaign: 'Edit Campaign',
        titleEn: 'Title (English)',
        titleTr: 'Title (Turkish)',
        titleAr: 'Title (Arabic)',
        descriptionEn: 'Description (English)',
        descriptionTr: 'Description (Turkish)',
        descriptionAr: 'Description (Arabic)',
        price: 'Price',
        priceLabelEn: 'Price Label (English)',
        priceLabelTr: 'Price Label (Turkish)',
        priceLabelAr: 'Price Label (Arabic)',
        imageUrl: 'Image URL',
        campaignImage: 'Campaign Image',
        uploadImage: 'Click to upload image',
        imageFormat: '(JPG, PNG, WEBP up to 5MB)',
        active: 'Active',
        inactive: 'Inactive',
        displayOrder: 'Display Order',
        title_column: 'Title',
        price_column: 'Price',
        status: 'Status',
        order: 'Order',
        image: 'Image'
      },
      messagesMgmt: {
        title: 'Contact Messages',
        date: 'Date',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        service: 'Service',
        location: 'Location',
        message: 'Message',
        status: 'Status',
        pending: 'Pending',
        read: 'Read',
        noMessages: 'No messages found',
        messageDetails: 'Message Details',
        markAsRead: 'Mark as Read',
        close: 'Close',
        view: 'View',
        editMessage: 'Edit Message',
        updateSuccess: 'Message updated successfully',
        deleteSuccess: 'Message deleted successfully',
        markAsReadSuccess: 'Marked as read'
      }
    },
    auth: {
      adminLogin: 'Admin Login',
      accessDashboard: 'Access the Zeyn Tour dashboard',
      email: 'Email',
      password: 'Password',
      rememberMe: 'Remember me',
      signIn: 'Sign In',
      demoCredentials: 'Demo credentials:',
      loginSuccess: 'Login successful',
      welcomeMessage: 'Welcome to Zeyn Tour dashboard',
      accessDenied: 'Access denied. Admin privileges required.',
      unexpectedError: 'An unexpected error occurred',
      logout: 'Logout'
    }
  },
  tr: {
    nav: {
      home: 'Ana Sayfa',
      cars: 'Araç Filomuz',
      services: 'Hizmetler',
      campaigns: 'Kampanyalar',
      about: 'Hakkımızda',
      contact: 'İletişim',
      dashboard: 'Yönetim'
    },
    hero: {
      title: 'Türkiye\'de Premium Araç Kiralama',
      subtitle: 'Güvenilir ve konforlu araçlarımızla Türkiye\'yi keşfedin. Profesyonel hizmet garantili.',
      ctaRent: 'Şimdi Kirala',
      ctaContact: 'İletişime Geç',
      whatsapp: 'WhatsApp',
      phone: 'Telefon',
      driverService: 'Şoför Hizmeti'
    },
    featuredCars: {
      title: 'Öne Çıkan Araçlarımız',
      subtitle: 'Kiralık premium araç seçeneklerimizi keşfedin',
      viewAll: 'Tüm Araçları Görüntüle'
    },
    booking: {
      title: 'Mükemmel Aracınızı Bulun',
      pickupLocation: 'Alış Yeri',
      dropoffLocation: 'Bırakış Yeri',
      pickupDate: 'Alış Tarihi', 
      dropoffDate: 'Bırakış Tarihi',
      searchCars: 'Araç Ara',
      selectLocation: 'Konum seçin',
      pickupPlaceholder: 'İstanbul Havalimanı',
      dropoffPlaceholder: 'Sultanahmet',
      pickDatePlaceholder: 'Bir tarih seçin',
      locations: [
        { value: 'ist-airport', label: 'İstanbul Havalimanı (IST)' },
        { value: 'saw-airport', label: 'İstanbul Sabiha Gökçen Havalimanı (SAW)' },
        { value: 'sisli', label: 'İstanbul Şişli (Avprupa yakası)' },
        { value: 'fatih', label: 'İstanbul Fatih (Avprupa yakası)' },
        { value: 'levet', label: 'İstanbul Levet (Avprupa yakası)' },
        { value: 'sultanahmed', label: 'İstanbul Sultan Ahmed (Avprupa yakası)' },
        { value: 'kadikoy', label: 'İstanbul Kadıköy (Avprupa yakası)' },
        { value: 'viaport', label: 'İstanbul Viaport (Anadolu yakası)' }
      ],
      bookCar: 'Rezervasyon',
      fillForm: 'Rezervasyonu tamamlamak için lütfen bilgilerinizi doldurun',
      personalInfo: 'Kişisel Bilgiler',
      fullName: 'Ad Soyad',
      fullNamePlaceholder: 'Ahmet Yılmaz',
      phone: 'Telefon Numarası',
      email: 'E-posta',
      birthDate: 'Doğum Tarihi',
      birthPlace: 'Doğum Yeri',
      birthPlacePlaceholder: 'Şehir, Ülke',
      tcIdentityNumber: 'T.C. Kimlik Numarası',
      tcIdentityNumberPlaceholder: '12345678901',
      passportNumber: 'Pasaport Numarası',
      passportNumberPlaceholder: 'AB1234567',
      passportIssuedDate: 'Pasaport Veriliş Tarihi',
      passportIssuedBy: 'Pasaport Veren Makam',
      passportIssuedByPlaceholder: 'Ülke/Makam',
      licenseNumber: 'Ehliyet Numarası',
      licenseNumberPlaceholder: 'DL123456',
      licenseIssuedDate: 'Ehliyet Veriliş Tarihi',
      licenseIssuedBy: 'Ehliyet Veren Makam',
      licenseIssuedByPlaceholder: 'Ülke/Makam',
      localAddress: 'Ev Adresi',
      localAddressPlaceholder: 'Tam adres',
      localPhone: 'Ev Telefonu',
      businessAddress: 'İş Adresi',
      businessAddressPlaceholder: 'İş adresi',
      businessPhone: 'İş Telefonu',
      invoiceAddress: 'Fatura Adresi',
      invoiceAddressPlaceholder: 'Fatura adresi',
      taxOffice: 'Vergi Dairesi',
      taxOfficePlaceholder: 'Vergi dairesi adı',
      taxNumber: 'Vergi Numarası',
      taxNumberPlaceholder: 'Vergi numarası',
      selectDate: 'Tarih seçin',
      rentalDetails: 'Kiralama Detayları',
      additionalInfo: 'Ek Bilgiler',
      secondDriverName: 'İkinci Sürücü Adı',
      secondDriverLicense: 'İkinci Sürücü Ehliyeti',
      secondDriverPassport: 'İkinci Sürücü Pasaportu',
      secondDriverNamePlaceholder: 'Ad Soyad',
      secondDriverLicensePlaceholder: 'Ehliyet numarası',
      secondDriverPassportPlaceholder: 'Pasaport numarası',
      paymentAmount1: 'Ödeme Tutarı 1',
      paymentAmount2: 'Ödeme Tutarı 2',
      sameAsPickup: 'Alış yeri ile aynı',
      pickupTime: 'Alış Saati',
      dropoffTime: 'Bırakış Saati',
      flightCode: 'Uçuş Kodu',
      optional: 'opsiyonel',
      withDriver: 'Şoförlü',
      rentalPeriod: 'Kiralama Süresi',
      days: 'gün',
      pricePerDay: 'Günlük Fiyat',
      totalPrice: 'Toplam Fiyat',
      cancel: 'İptal',
      confirmBooking: 'Rezervasyonu Onayla',
      submitting: 'Gönderiliyor...',
      fillRequired: 'Lütfen tüm gerekli alanları doldurun',
      invalidDates: 'Bırakış tarihi alış tarihinden sonra olmalıdır',
      success: 'Rezervasyon başarıyla gönderildi! En kısa sürede sizinle iletişime geçeceğiz.',
      error: 'Rezervasyon oluşturulamadı. Lütfen tekrar deneyin.',
      searching: 'Müsait araçlar aranıyor...'
    },
    services: {
      title: 'Hizmetlerimiz',
      subtitle: 'Türkiye\'de konforunuz ve rahatlığınız için tasarlanmış kapsamlı araç kiralama hizmetleri',
      subtitleShort: 'Türkiye\'de konforunuz için özel olarak tasarlanmış profesyonel araç kiralama hizmetleri',
      viewAllServices: 'Tüm Hizmetleri Gör',
      premiumServices: 'Premium Hizmetler',
      professionalDrivers: 'Profesyonel Şoförler',
      available247: '7/24 Müsait',
      insuranceIncluded: 'Sigorta Dahil',
      allIstanbulAirports: 'Tüm İstanbul Havalimanları',
      flightTracking: 'Uçuş Takibi',
      meetGreet: 'Karşılama Hizmeti',
      hotelDelivery: 'Otel Teslimi',
      cityCenter: 'Şehir Merkezi',
      freeService: 'Ücretsiz Hizmet',
      learnMore: 'Daha Fazla Bilgi',
      fullyInsuredTitle: 'Tam Sigortalı',
      completeCoverage: 'Kapsamlı teminat',
      support247Title: '7/24 Destek',
      alwaysAvailable: 'Her zaman ulaşılabilir',
      premiumFleet: 'Premium Filo',
      latestModels: 'En son modeller',
      fiveStarServiceTitle: '5 Yıldızlı Hizmet',
      customerRated: 'Müşteri değerlendirmeli',
      additionalServices: 'Ek Hizmetler',
      whyChoose: 'Neden Zeyn Tour?',
      getQuote: 'Fiyat Al',
      addToBooking: 'Rezervasyona Ekle',
      readyToBook: 'Rezervasyon Yapmaya Hazır mısınız?',
      contactNow: 'Gereksinimlerinizi görüşmek ve kişiselleştirilmiş bir fiyat teklifi almak için şimdi bizimle iletişime geçin.',
      whatsappUs: 'WhatsApp ile İletişim',
      callNow: 'Şimdi Ara',
      withDriver: 'Şoförlü Araç',
      withDriverDesc: 'Konforunuz ve güvenliğiniz için profesyonel şoför hizmeti',
      airportPickup: 'Havaalanı Transferi',
      airportPickupDesc: 'İstanbul havaalanlarından direkt alım hizmeti',
      delivery: 'Araç Teslimatı',
      deliveryDesc: 'Kiralık aracınızı İstanbul\'da istediğiniz yere teslim ediyoruz',
      fullyInsured: 'Tam Sigortalı',
      fullyInsuredDesc: 'Tüm araçlarda kapsamlı sigorta teminatı',
      support247: '7/24 Destek',
      support247Desc: 'Sürekli müşteri hizmeti ve yol yardımı',
      fiveStarService: '5 Yıldızlı Hizmet',
      fiveStarServiceDesc: 'Binlerce memnun müşteri tarafından mükemmel olarak değerlendirildi',
      nationwideCoverage: 'Ülke Çapında Hizmet',
      nationwideCoverageDesc: 'Türkiye\'nin tüm büyük şehirlerinde hizmet mevcuttur',
      perDay: 'günlük',
      featuresLabel: 'Özellikler'
    },
    contact: {
      title: 'İletişim',
      subtitle: 'Türkiye genelinde premium araç kiralama hizmetleri için bizimle iletişime geçin. 7/24 yardımınızdayız.',
      sendMessage: 'Bize Mesaj Gönderin',
      formDescription: 'Aşağıdaki formu doldurun, 24 saat içinde size geri döneceğiz',
      fullName: 'Ad Soyad',
      email: 'E-posta',
      phone: 'Telefon Numarası',
      service: 'İhtiyaç Duyulan Hizmet',
      location: 'Tercih Edilen Konum',
      message: 'Mesaj',
      sending: 'Gönderiliyor...',
      sendMsg: 'Mesaj Gönder',
      quickContact: 'Hızlı İletişim',
      quickContactDesc: 'Acil yardıma mı ihtiyacınız var? Doğrudan bizimle iletişime geçin',
      businessHours: 'Çalışma Saatleri',
      followUs: 'Bizi Takip Edin',
      website: 'Website',
      workingHours: 'Çalışma Saatleri',
      emergencySupport: 'Acil Destek',
      alwaysAvailable: 'Her Zaman Ulaşılabilir',
      operateDesc: 'İhtiyacınız olduğunda hizmetlerimize erişebilmenizi sağlamak için 7/24 çalışıyoruz.',
      serviceLocations: 'Hizmet Lokasyonlarımız',
      serviceAreas: 'Hizmet Bölgeleri',
      serviceAreasDesc: 'İstanbul, Ankara, Antalya, İzmir ve tüm büyük Türk şehirleri. Havalimanı transfer ve şehir içi teslimat mevcut.',
      mondaySunday: 'Pazartesi - Pazar',
      available247: '7/24 Müsait',
      generalInquiries: 'Genel Sorular',
      reservations: 'Rezervasyonlar',
      driverService: 'Şoför Hizmeti',
      whatsapp: 'WhatsApp',
      coverageAreas: 'Kapsama Alanları',
      selectService: 'Bir hizmet seçin',
      selectLocation: 'Konum seçin',
      carRental: 'Araç Kiralama',
      withDriver: 'Şoförlü Araç',
      airportTransfer: 'Havaalanı Transferi',
      longTerm: 'Uzun Süreli Kiralama',
      other: 'Diğer',
      istanbul: 'İstanbul',
      ankara: 'Ankara',
      antalya: 'Antalya',
      izmir: 'İzmir',
      otherCity: 'Diğer Şehir',
      contactMethod: {
        generalInq: 'Genel Sorular',
        reservations: 'Rezervasyonlar',
        driver: 'Şoför Hizmeti',
        driverservice: 'Şoför Hizmeti',
        whatsapp: 'WhatsApp',
        callDesc: 'Acil yardım için bizi arayın',
        bookDesc: 'Araç kiralamanızı yapın',
        chauffeurDesc: 'Şoför ve sürücü hizmetleri',
        chatDesc: 'Anında bizimle sohbet edin'
      },
      locations: {
        istanbul: 'İstanbul',
        istanbulDesc: 'Ana ofis ve en büyük filomuz',
        ankara: 'Ankara',
        ankaraDesc: 'Başkent hizmet merkezi',
        antalya: 'Antalya',
        antalyaDesc: 'Turizm merkezi operasyonları',
        izmir: 'İzmir',
        izmirDesc: 'Ege bölgesi kapsamı'
      },
      form: {
        nameLabel: 'Ad Soyad',
        emailLabel: 'E-posta',
        phoneLabel: 'Telefon Numarası',
        servicePlaceholder: 'Bir hizmet seçin',
        locationPlaceholder: 'Konum seçin',
        messagePlaceholder: 'Kiralama ihtiyaçlarınız, tarihler, tercihler hakkında bize bilgi verin...'
      }
    },
    about: {
      badge: 'Kuruluş 2015',
      title: 'Zeyn Tour\'a Hoş Geldiniz',
      subtitle: 'Her Yolculuk Bir Gülümsemeyle Başlar (ve Gerçekten Güzel Bir Araçla)',
      intro: 'Biz sadece bir araç kiralama şirketi değiliz. Bir araca ihtiyacınız olduğunda aradığınız arkadaşlar, kahvenizi nasıl sevdiğinizi hatırlayan insanlar ve evet, araçlarımızı tertemiz tutmak için takıntılı olan kişileriz.',
      happyCustomers: 'Mutlu Müşteri',
      premiumCars: 'Premium Araç',
      citiesCovered: 'Hizmet Verilen Şehir',
      coffeeDrunk: 'İçilen Kahve',
      ourStory: 'Hikayemiz',
      storyTagline: 'Ya da: Arabalara Olan Tutkumuzu Mükemmel Yolculuğunuza Nasıl Dönüştürdük',
      storyPara1: 'Her şey 2015\'te iki arkadaş, üç araba ve mantıksız miktarda iyimserlikle başladı. Araç kiralamanın bir iş gibi hissetmemesi gerektiğine inanıyorduk - bir maceranın başlangıcı gibi hissettirmeliydi.',
      storyPara2: 'Bugüne kadar: 10.000\'den fazla müşteriye Türkiye\'yi şık bir şekilde keşfetmelerinde yardımcı olduk. Bazıları iş için geldi, diğerleri zevk için, birkaçı da sadece araçlarımızın söylediğimiz kadar güzel olup olmadığını görmek istedi (spoiler: öyleler).',
      storyPara3: 'Sırrımız mı? Her aracı kendimizinmiş, her müşteriyi ailemizmiş gibi davranıyoruz. Tabii ki size atıştırmalık getiren ve akşam yemeğinde garip sorular sormayan havalı aile türünden.',
      meetTeam: 'Ekibimizle Tanışın',
      teamTagline: 'Direksiyonların Arkasındaki İnsanlar',
      ceo: 'Vizyonerler',
      ceoDesc: 'Kurucularımız hala insanların tatil planlarına güvenmesine inanamıyor. Profesyonel olduğumuza söz veriyoruz (çoğu zaman).',
      drivers: 'Şoförlerimiz',
      driversDesc: 'Kısmen GPS, kısmen tur rehberi, kısmen komedyen. Türkiye\'deki her kestirme yolu, her iyi restoranı ve her kötü şakayı bilirler.',
      support: 'Müşteri Destek Kahramanları',
      supportDesc: '7/24 müsaitlik, pijamalarıyla sabah 3\'te soruları cevapladıkları anlamına gelir, itiraf etmek istediklerinden daha fazla.',
      mechanics: 'Araba Fısıldayıcıları',
      mechanicsDesc: 'Bir sorunu olmadan önce duyabilirler. Cidden, neredeyse sihirli. Ya da sadece arabaları çok kontrol ediyorlar. Muhtemelen bu.',
      ourValues: 'Neyi Savunuyoruz',
      valuesTagline: 'Gerçekten Temiz Arabalar Dışında',
      value1Title: 'Dürüstlük',
      value1Desc: 'Gizli ücret yok, sürpriz yok. Belki sizi sevdiğimiz için bir yükseltme bulmanız hariç.',
      value2Title: 'Neşe',
      value2Desc: 'Hayat sıkıcı araç kiralamaları için çok kısa. Eğlenceyi biz getiriyoruz (ve rahat koltukları).',
      value3Title: 'Mükemmellik',
      value3Desc: 'Mükemmeliyetçiyiz. Araba başına 2 saat harcayarak onu parlatan detay ekibimize sorun.'
    },
    cars: {
      title: 'Premium Filomuz',
      subtitle: 'Türkiye\'deki mükemmel yolculuğunuz için premium araç filomuzdan seçim yapın',
      filterCars: 'Araçları Filtrele',
      searchCars: 'Araç ara...',
      brand: 'Marka',
      allBrands: 'Tüm Markalar',
      transmission: 'Vites',
      allTransmissions: 'Tüm Vitesler',
      automatic: 'Otomatik',
      manual: 'Manuel',
      fuelType: 'Yakıt Türü',
      allFuelTypes: 'Tüm Yakıt Türleri',
      petrol: 'Benzin',
      diesel: 'Dizel',
      petrolDiesel: 'Benzin/Dizel',
      hybrid: 'Hibrit',
      electric: 'Elektrik',
      seats: 'Koltuk',
      anySeats: 'Herhangi Koltuk',
      luggage: 'Bagaj',
      luggageCapacity: 'Bavul',
      minLicenseAge: 'Min. Ehliyet Yaşı',
      years: 'yıl',
      priceRange: 'Fiyat Aralığı',
      anyPrice: 'Herhangi Fiyat',
      available: 'Müsait',
      bookNow: 'Şimdi Rezerve Et',
      viewDetails: 'Detayları Görüntüle',
      perDay: 'günlük',
      showing: 'Gösteriliyor',
      of: '/',
      vehicles: 'araç',
      noCarsFound: 'Araç bulunamadı',
      adjustFilters: 'Daha fazla sonuç görmek için filtrelerinizi ayarlayın',
      features: 'Özellikler',
      moreFeatures: 'daha fazla',
      year: 'Yıl'
    },
    carDetails: {
      backToCars: 'Araçlara Dön',
      unavailable: 'Müsait Değil',
      specifications: 'Özellikler',
      people: 'kişi',
      location: 'Konum',
      brand: 'Marka',
      features: 'Özellikler',
      pricing: 'Fiyatlandırma',
      dailyRate: 'Günlük Ücret',
      weeklyRate: 'Haftalık Ücret',
      monthlyRate: 'Aylık Ücret',
      yearlyRate: 'Yıllık Ücret',
      contactWhatsApp: 'WhatsApp ile İletişime Geç',
      bookNow: 'Şimdi Rezerve Et'
    },
    carFeatures: {
      'manual transmission': 'Manuel Vites',
      'automatic transmission': 'Otomatik Vites',
      'economic': 'Ekonomik',
      'air conditioning': 'Klima',
      'bluetooth': 'Bluetooth',
      'gps navigation': 'GPS Navigasyon',
      'usb charging': 'USB Şarj',
      'backup camera': 'Geri Görüş Kamerası',
      'cruise control': 'Hız Sabitleyici',
      'sunroof': 'Sunroof',
      'leather seats': 'Deri Koltuklar',
      'heated seats': 'Isıtmalı Koltuklar',
      'premium audio': 'Premium Ses Sistemi',
      'digital dashboard': 'Dijital Gösterge Paneli',
      'advanced safety': 'Gelişmiş Güvenlik',
      'hybrid engine': 'Hibrit Motor',
      'all weather': 'Her Mevsim',
      'high seating': 'Yüksek Oturma',
      'suv design': 'SUV Tasarım',
      'compact design': 'Kompakt Tasarım',
      'spacious interior': 'Geniş İç Mekan',
      'fuel efficient': 'Yakıt Tasarruflu',
      'modern tech': 'Modern Teknoloji'
    },
    footer: {
      description: 'Türkiye\'de premium araç kiralama hizmetleri. Profesyonel şoförler, lüks araçlar ve tüm ulaşım ihtiyaçlarınız için olağanüstü hizmet.',
      quickLinks: 'Hızlı Bağlantılar',
      home: 'Ana Sayfa',
      ourFleet: 'Araç Filomuz',
      services: 'Hizmetler',
      campaigns: 'Kampanyalar',
      contact: 'İletişim',
      deliveryForm: 'Teslim Formu',
      approvedEcommerce: 'Onaylı E-Ticaret Güvencesi',
      contactInfo: 'İletişim Bilgileri',
      serviceInCities: 'Tüm büyük şehirlerde hizmet',
      copyright: '© 2024 Zeyn Tour. Tüm hakları saklıdır.',
      privacyPolicy: 'Gizlilik Politikası',
      termsOfService: 'Hizmet Şartları',
      admin: 'Yönetici',
      poweredBy: 'Prebitit tarafından desteklenmektedir'
    },
    campaigns: {
      title: 'Özel Kampanyalar',
      subtitle: 'Araç kiralama ihtiyaçlarınız için özel tekliflerimizi ve kampanyalarımızı keşfedin',
      bookNow: 'Şimdi Rezerve Et',
      backToCampaigns: 'Kampanyalara Dön',
      forRentals: '3 GÜN VE DAHA UZUN KİRALAMALAR İÇİN',
      startingFrom: 'BAŞLANGIÇ'
    },
    dashboard: {
      title: 'Yönetici Paneli',
      tabs: {
        cars: 'Araçları Yönet',
        bookings: 'Rezervasyonları Yönet',
        services: 'Hizmetleri Yönet',
        campaigns: 'Kampanyaları Yönet',
        messages: 'İletişim Mesajları',
        analytics: 'Analitik'
      },
      loading: 'Yükleniyor',
      manage: {
        addNew: 'Yeni Ekle',
        edit: 'Düzenle',
        delete: 'Sil',
        cancel: 'İptal',
        save: 'Kaydet',
        update: 'Güncelle',
        create: 'Oluştur',
        actions: 'İşlemler',
        confirmDelete: 'Bu öğeyi silmek istediğinizden emin misiniz?',
        uploading: 'Yükleniyor...',
        noDataFound: 'Veri bulunamadı'
      },
      bookingsMgmt: {
        title: 'Rezervasyonları Yönet',
        addNew: 'Yeni Rezervasyon Ekle',
        createNew: 'Yeni Rezervasyon Oluştur',
        customerName: 'Müşteri Adı',
        car: 'Araç',
        customerEmail: 'E-posta',
        customerPhone: 'Telefon',
        pickupLocation: 'Alış Yeri',
        dropoffLocation: 'Bırakış Yeri',
        pickupDate: 'Alış Tarihi',
        dropoffDate: 'Bırakış Tarihi',
        totalPrice: 'Toplam Fiyat',
        status: 'Durum',
        withDriver: 'Şoförlü',
        notes: 'Notlar',
        days: 'Gün',
        total: 'Toplam',
        driver: 'Şoför',
        contact: 'İletişim',
        pickup: 'Alış',
        dropoff: 'Bırakış',
        dates: 'Tarihler',
        sameAsPickup: 'Alış ile aynı',
        pending: 'Beklemede',
        confirmed: 'Onaylandı',
        cancelled: 'İptal Edildi',
        yes: 'Evet',
        no: 'Hayır'
      },
      carsMgmt: {
        title: 'Araçları Yönet',
        addNew: 'Yeni Araç Ekle',
        editCar: 'Aracı Düzenle',
        carImages: 'Araç Görselleri',
        uploadImages: 'Görsel yüklemek için tıklayın',
        imageFormat: '(JPG, PNG, WEBP 5MB\'ye kadar)',
        youtubeLink: 'YouTube Video Linki (Opsiyonel)',
        brand: 'Marka',
        model: 'Model',
        year: 'Yıl',
        seats: 'Koltuk',
        luggage: 'Bagaj Kapasitesi',
        luggageCapacity: 'Bavul',
        minLicenseAge: 'Min. Ehliyet Yaşı',
        transmission: 'Vites',
        automatic: 'Otomatik',
        manual: 'Manuel',
        fuelType: 'Yakıt Türü',
        petrol: 'Benzin',
        diesel: 'Dizel',
        petrolDiesel: 'Benzin/Dizel',
        hybrid: 'Hibrit',
        electric: 'Elektrik',
        dailyPrice: 'Günlük Fiyat',
        weeklyPrice: 'Haftalık Fiyat',
        monthlyPrice: 'Aylık Fiyat',
        yearlyPrice: 'Yıllık Fiyat',
        optional: 'Opsiyonel',
        location: 'Konum',
        availability: 'Müsaitlik',
        available: 'Müsait',
        notAvailable: 'Müsait Değil',
        image: 'Görsel',
        brandModel: 'Marka & Model',
        pricePerDay: 'Fiyat/Gün',
        actions: 'İşlemler',
        noImage: 'Görsel Yok',
        addCar: 'Araç Ekle',
        updateCar: 'Aracı Güncelle',
        vehiclePlate: 'Araç Plakası',
        vehicleGroup: 'Araç Grubu'
      },
      servicesMgmt: {
        title: 'Hizmetleri Yönet',
        addNew: 'Yeni Hizmet Ekle',
        editService: 'Hizmeti Düzenle',
        nameEn: 'İsim (İngilizce)',
        nameTr: 'İsim (Türkçe)',
        nameAr: 'İsim (Arapça)',
        descriptionEn: 'Açıklama (İngilizce)',
        descriptionTr: 'Açıklama (Türkçe)',
        descriptionAr: 'Açıklama (Arapça)',
        price: 'Fiyat',
        priceType: 'Fiyat Türü',
        perDay: 'Günlük',
        perWeek: 'Haftalık',
        perMonth: 'Aylık',
        fixed: 'Sabit',
        active: 'Aktif',
        inactive: 'Pasif',
        name: 'İsim',
        description: 'Açıklama',
        status: 'Durum',
        image: 'Görsel'
      },
      analyticsMgmt: {
        title: 'Analitik Özet',
        totalCars: 'Toplam Araç',
        activeCars: 'Aktif Araçlar',
        totalBookings: 'Toplam Rezervasyon',
        activeBookings: 'Aktif Rezervasyonlar',
        totalRevenue: 'Toplam Gelir',
        thisMonth: 'Bu Ay',
        avgBookingValue: 'Ort. Rezervasyon Değeri',
        perBooking: 'rezervasyon başına',
        fleetUtilization: 'Filo Kullanımı',
        carsInUse: 'Kullanımdaki araçlar',
        bookingRate: 'Rezervasyon Oranı',
        bookingsPerCar: 'Araç başına rezervasyon',
        quickInsights: 'Hızlı Bilgiler',
        mostPopularService: 'En Popüler Hizmet',
        carRentalWithDriver: 'Şoförlü araç kiralama',
        peakSeason: 'Yoğun Sezon',
        summerMonths: 'Yaz ayları (Haz-Ağu)',
        trending: 'Trend',
        seasonal: 'Mevsimsel'
      },
      campaignsMgmt: {
        title: 'Kampanyaları Yönet',
        addNew: 'Yeni Kampanya Ekle',
        editCampaign: 'Kampanyayı Düzenle',
        titleEn: 'Başlık (İngilizce)',
        titleTr: 'Başlık (Türkçe)',
        titleAr: 'Başlık (Arapça)',
        descriptionEn: 'Açıklama (İngilizce)',
        descriptionTr: 'Açıklama (Türkçe)',
        descriptionAr: 'Açıklama (Arapça)',
        price: 'Fiyat',
        priceLabelEn: 'Fiyat Etiketi (İngilizce)',
        priceLabelTr: 'Fiyat Etiketi (Türkçe)',
        priceLabelAr: 'Fiyat Etiketi (Arapça)',
        imageUrl: 'Görsel URL',
        campaignImage: 'Kampanya Görseli',
        uploadImage: 'Görsel yüklemek için tıklayın',
        imageFormat: '(JPG, PNG, WEBP 5MB\'ye kadar)',
        active: 'Aktif',
        inactive: 'Pasif',
        displayOrder: 'Gösterim Sırası',
        title_column: 'Başlık',
        price_column: 'Fiyat',
        status: 'Durum',
        order: 'Sıra',
        image: 'Görsel'
      },
      messagesMgmt: {
        title: 'İletişim Mesajları',
        date: 'Tarih',
        name: 'İsim',
        email: 'E-posta',
        phone: 'Telefon',
        service: 'Hizmet',
        location: 'Konum',
        message: 'Mesaj',
        status: 'Durum',
        pending: 'Beklemede',
        read: 'Okundu',
        noMessages: 'Mesaj bulunamadı',
        messageDetails: 'Mesaj Detayları',
        markAsRead: 'Okundu Olarak İşaretle',
        close: 'Kapat',
        view: 'Görüntüle',
        editMessage: 'Mesajı Düzenle',
        updateSuccess: 'Mesaj başarıyla güncellendi',
        deleteSuccess: 'Mesaj başarıyla silindi',
        markAsReadSuccess: 'Okundu olarak işaretlendi'
      }
    },
    auth: {
      adminLogin: 'Yönetici Girişi',
      accessDashboard: 'Zeyn Tour kontrol paneline erişin',
      email: 'E-posta',
      password: 'Şifre',
      rememberMe: 'Beni hatırla',
      signIn: 'Giriş Yap',
      demoCredentials: 'Demo giriş bilgileri:',
      loginSuccess: 'Giriş başarılı',
      welcomeMessage: 'Zeyn Tour kontrol paneline hoş geldiniz',
      accessDenied: 'Erişim reddedildi. Yönetici yetkisi gerekli.',
      unexpectedError: 'Beklenmeyen bir hata oluştu',
      logout: 'Çıkış Yap'
    }
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      cars: 'أسطول السيارات',
      services: 'الخدمات',
      campaigns: 'الحملات',
      about: 'من نحن',
      contact: 'اتصل بنا',
      dashboard: 'لوحة التحكم'
    },
    hero: {
      title: 'تأجير سيارات فاخرة في تركيا',
      subtitle: 'اكتشف تركيا بسياراتنا الموثوقة والمريحة. خدمة احترافية مضمونة.',
      ctaRent: 'احجز الآن',
      ctaContact: 'اتصل بنا',
      whatsapp: 'واتساب',
      phone: 'الهاتف',
      driverService: 'خدمة السائق'
    },
    featuredCars: {
      title: 'سياراتنا المميزة',
      subtitle: 'استكشف مجموعتنا من السيارات الفاخرة المتاحة للإيجار',
      viewAll: 'عرض جميع السيارات'
    },
    booking: {
      title: 'ابحث عن السيارة المثالية',
      pickupLocation: 'مكان الاستلام',
      dropoffLocation: 'مكان التسليم',
      pickupDate: 'تاريخ الاستلام',
      dropoffDate: 'تاريخ التسليم', 
      searchCars: 'ابحث عن السيارات',
      selectLocation: 'اختر الموقع',
      pickupPlaceholder: 'مطار إسطنبول',
      dropoffPlaceholder: 'السلطان أحمد',
      pickDatePlaceholder: 'اختر تاريخاً',
      locations: [
        { value: 'ist-airport', label: 'مطار إسطنبول (IST)' },
        { value: 'saw-airport', label: 'مطار إسطنبول صبيحة كوكجن (SAW)' },
        { value: 'sisli', label: 'اسطنبول شيشلي (الجانب الأوروبي)' },
        { value: 'fatih', label: 'اسطنبول الفاتح (الجانب الأوروبي)' },
        { value: 'levet', label: 'اسطنبول ليفيت (الجانب الأوروبي)' },
        { value: 'sultanahmed', label: 'اسطنبول سلطان أحمد (الجانب الأوروبي)' },
        { value: 'kadikoy', label: 'اسطنبول كاديكوي (الجانب الأوروبي)' },
        { value: 'viaport', label: 'ميناء اسطنبول (الجانب الأناضولي)' }
      ],
      bookCar: 'احجز السيارة',
      fillForm: 'يرجى ملء بياناتك لإتمام الحجز',
      personalInfo: 'المعلومات الشخصية',
      fullName: 'الاسم الكامل',
      fullNamePlaceholder: 'أحمد محمد',
      phone: 'رقم الهاتف',
      email: 'البريد الإلكتروني',
      birthDate: 'تاريخ الميلاد',
      birthPlace: 'مكان الميلاد',
      birthPlacePlaceholder: 'المدينة، البلد',
      tcIdentityNumber: 'رقم الهوية التركية',
      tcIdentityNumberPlaceholder: '12345678901',
      passportNumber: 'رقم جواز السفر',
      passportNumberPlaceholder: 'AB1234567',
      passportIssuedDate: 'تاريخ إصدار جواز السفر',
      passportIssuedBy: 'جهة إصدار جواز السفر',
      passportIssuedByPlaceholder: 'البلد/الجهة',
      licenseNumber: 'رقم رخصة القيادة',
      licenseNumberPlaceholder: 'DL123456',
      licenseIssuedDate: 'تاريخ إصدار الرخصة',
      licenseIssuedBy: 'جهة إصدار الرخصة',
      licenseIssuedByPlaceholder: 'البلد/الجهة',
      localAddress: 'العنوان المحلي',
      localAddressPlaceholder: 'العنوان الكامل',
      localPhone: 'الهاتف المحلي',
      businessAddress: 'عنوان العمل',
      businessAddressPlaceholder: 'عنوان العمل',
      businessPhone: 'هاتف العمل',
      invoiceAddress: 'عنوان الفاتورة',
      invoiceAddressPlaceholder: 'عنوان الفاتورة',
      taxOffice: 'مكتب الضرائب',
      taxOfficePlaceholder: 'اسم مكتب الضرائب',
      taxNumber: 'الرقم الضريبي',
      taxNumberPlaceholder: 'الرقم الضريبي',
      selectDate: 'اختر التاريخ',
      rentalDetails: 'تفاصيل الإيجار',
      additionalInfo: 'معلومات إضافية',
      secondDriverName: 'اسم السائق الثاني',
      secondDriverLicense: 'رخصة السائق الثاني',
      secondDriverPassport: 'جواز سفر السائق الثاني',
      secondDriverNamePlaceholder: 'الاسم الكامل',
      secondDriverLicensePlaceholder: 'رقم الرخصة',
      secondDriverPassportPlaceholder: 'رقم جواز السفر',
      paymentAmount1: 'مبلغ الدفع 1',
      paymentAmount2: 'مبلغ الدفع 2',
      sameAsPickup: 'نفس مكان الاستلام',
      pickupTime: 'وقت الاستلام',
      dropoffTime: 'وقت التسليم',
      flightCode: 'رمز الرحلة',
      optional: 'اختياري',
      withDriver: 'مع سائق',
      rentalPeriod: 'فترة الإيجار',
      days: 'يوم',
      pricePerDay: 'السعر لليوم',
      totalPrice: 'السعر الإجمالي',
      cancel: 'إلغاء',
      confirmBooking: 'تأكيد الحجز',
      submitting: 'جارٍ الإرسال...',
      fillRequired: 'يرجى ملء جميع الحقول المطلوبة',
      invalidDates: 'يجب أن يكون تاريخ التسليم بعد تاريخ الاستلام',
      success: 'تم إرسال الحجز بنجاح! سنتواصل معك قريباً.',
      error: 'فشل إنشاء الحجز. يرجى المحاولة مرة أخرى.',
      searching: 'البحث عن السيارات المتاحة...'
    },
    services: {
      title: 'خدماتنا',
      subtitle: 'خدمات تأجير سيارات شاملة مصممة لراحتك في تركيا',
      subtitleShort: 'خدمات تأجير سيارات احترافية مصممة خصيصاً لراحتك في تركيا',
      viewAllServices: 'عرض جميع الخدمات',
      premiumServices: 'الخدمات المميزة',
      professionalDrivers: 'سائقون محترفون',
      available247: 'متاح 24/7',
      insuranceIncluded: 'التأمين مشمول',
      allIstanbulAirports: 'جميع مطارات إسطنبول',
      flightTracking: 'تتبع الرحلات',
      meetGreet: 'خدمة الاستقبال',
      hotelDelivery: 'توصيل الفندق',
      cityCenter: 'وسط المدينة',
      freeService: 'خدمة مجانية',
      learnMore: 'اعرف المزيد',
      fullyInsuredTitle: 'مؤمنة بالكامل',
      completeCoverage: 'تغطية شاملة',
      support247Title: 'دعم 24/7',
      alwaysAvailable: 'متاح دائماً',
      premiumFleet: 'أسطول مميز',
      latestModels: 'أحدث الموديلات',
      fiveStarServiceTitle: 'خدمة 5 نجوم',
      customerRated: 'تقييم العملاء',
      additionalServices: 'الخدمات الإضافية',
      whyChoose: 'لماذا تختار زين تور؟',
      getQuote: 'احصل على عرض سعر',
      addToBooking: 'أضف إلى الحجز',
      readyToBook: 'جاهز للحجز؟',
      contactNow: 'اتصل بنا الآن لمناقشة متطلباتك والحصول على عرض أسعار مخصص لاحتياجات تأجير السيارات.',
      whatsappUs: 'راسلنا على واتساب',
      callNow: 'اتصل الآن',
      withDriver: 'سيارة مع سائق',
      withDriverDesc: 'خدمة سائق محترف لراحتك وأمانك',
      airportPickup: 'نقل المطار',
      airportPickupDesc: 'خدمة استقبال مباشرة من مطارات إسطنبول',
      delivery: 'توصيل السيارة',
      deliveryDesc: 'نوصل لك السيارة المؤجرة إلى أي مكان في إسطنبول',
      fullyInsured: 'مؤمنة بالكامل',
      fullyInsuredDesc: 'تغطية تأمينية شاملة على جميع المركبات',
      support247: 'دعم 24/7',
      support247Desc: 'خدمة عملاء ومساعدة على الطريق على مدار الساعة',
      fiveStarService: 'خدمة 5 نجوم',
      fiveStarServiceDesc: 'تقييم ممتاز من آلاف العملاء الراضين',
      nationwideCoverage: 'تغطية على مستوى البلاد',
      nationwideCoverageDesc: 'الخدمة متاحة في جميع المدن التركية الكبرى',
      perDay: 'في اليوم',
      featuresLabel: 'الميزات'
    },
    contact: {
      title: 'اتصل بنا',
      subtitle: 'تواصل معنا للحصول على خدمات تأجير سيارات مميزة في جميع أنحاء تركيا. نحن هنا للمساعدة على مدار الساعة.',
      sendMessage: 'أرسل لنا رسالة',
      formDescription: 'املأ النموذج أدناه وسنرد عليك خلال 24 ساعة',
      fullName: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      service: 'الخدمة المطلوبة',
      location: 'الموقع المفضل',
      message: 'الرسالة',
      sending: 'جارٍ الإرسال...',
      sendMsg: 'إرسال الرسالة',
      quickContact: 'اتصال سريع',
      quickContactDesc: 'تحتاج مساعدة فورية؟ اتصل بنا مباشرة',
      businessHours: 'ساعات العمل',
      followUs: 'تابعنا',
      website: 'الموقع الإلكتروني',
      workingHours: 'ساعات العمل',
      emergencySupport: 'الدعم الطارئ',
      alwaysAvailable: 'متاح دائماً',
      operateDesc: 'نعمل على مدار الساعة لضمان وصولك لخدماتنا عند الحاجة.',
      serviceLocations: 'مواقع خدماتنا',
      serviceAreas: 'مناطق الخدمة',
      serviceAreasDesc: 'إسطنبول وأنقرة وأنطاليا وإزمير وجميع المدن التركية الكبرى. خدمة النقل من المطار والتوصيل داخل المدينة متاحة.',
      mondaySunday: 'الاثنين - الأحد',
      available247: 'متاح 24/7',
      generalInquiries: 'الاستفسارات العامة',
      reservations: 'الحجوزات',
      driverService: 'خدمة السائق',
      whatsapp: 'واتساب',
      coverageAreas: 'مناطق التغطية',
      selectService: 'اختر خدمة',
      selectLocation: 'اختر موقع',
      carRental: 'تأجير سيارات',
      withDriver: 'سيارة مع سائق',
      airportTransfer: 'نقل المطار',
      longTerm: 'إيجار طويل الأمد',
      other: 'أخرى',
      istanbul: 'إسطنبول',
      ankara: 'أنقرة',
      antalya: 'أنطاليا',
      izmir: 'إزمير',
      otherCity: 'مدينة أخرى',
      contactMethod: {
        generalInq: 'الاستفسارات العامة',
        reservations: 'الحجوزات',
        driver: 'خدمة السائق',
        driverservice: 'خدمة السائق',
        whatsapp: 'واتساب',
        callDesc: 'اتصل بنا للحصول على مساعدة فورية',
        bookDesc: 'احجز تأجير سيارتك',
        chauffeurDesc: 'خدمات السائق والسيارة مع سائق',
        chatDesc: 'تحدث معنا فوراً'
      },
      locations: {
        istanbul: 'إسطنبول',
        istanbulDesc: 'المكتب الرئيسي وأكبر أسطول',
        ankara: 'أنقرة',
        ankaraDesc: 'مركز خدمة العاصمة',
        antalya: 'أنطاليا',
        antalyaDesc: 'عمليات مركز السياحة',
        izmir: 'إزمير',
        izmirDesc: 'تغطية منطقة بحر إيجه'
      },
      form: {
        nameLabel: 'الاسم الكامل',
        emailLabel: 'البريد الإلكتروني',
        phoneLabel: 'رقم الهاتف',
        servicePlaceholder: 'اختر خدمة',
        locationPlaceholder: 'اختر موقع',
        messagePlaceholder: 'أخبرنا عن احتياجات التأجير والتواريخ والتفضيلات...'
      }
    },
    about: {
      badge: 'تأسست 2015',
      title: 'مرحباً بكم في زين تور',
      subtitle: 'حيث تبدأ كل رحلة بابتسامة (وسيارة رائعة حقاً)',
      intro: 'نحن لسنا مجرد شركة تأجير سيارات أخرى. نحن الأصدقاء الذين تتصل بهم عندما تحتاج إلى سيارة، الأشخاص الذين يتذكرون كيف تحب قهوتك، ونعم، الأشخاص المهووسون بإبقاء سياراتنا نظيفة تماماً.',
      happyCustomers: 'عميل سعيد',
      premiumCars: 'سيارة فاخرة',
      citiesCovered: 'مدينة مغطاة',
      coffeeDrunk: 'قهوة مستهلكة',
      ourStory: 'قصتنا',
      storyTagline: 'أو: كيف حولنا شغفنا بالسيارات إلى رحلتك المثالية',
      storyPara1: 'بدأ كل شيء في عام 2015 مع صديقين وثلاث سيارات وكمية غير معقولة من التفاؤل. كنا نؤمن بأن استئجار السيارة لا ينبغي أن يبدو وكأنه عمل روتيني - بل يجب أن يشعر وكأنه بداية مغامرة.',
      storyPara2: 'سريعاً إلى اليوم: ساعدنا أكثر من 10,000 عميل على استكشاف تركيا بأناقة. جاء البعض للعمل، والبعض الآخر للمتعة، والقليل منهم أراد فقط معرفة ما إذا كانت سياراتنا جميلة كما قلنا (حذر من الحرق: إنها كذلك).',
      storyPara3: 'سرنا؟ نتعامل مع كل سيارة وكأنها لنا، ومع كل عميل وكأنه عائلة. حسناً، النوع الرائع من العائلة التي تجلب لك الوجبات الخفيفة ولا تطرح أسئلة محرجة على العشاء.',
      meetTeam: 'تعرف على الفريق',
      teamTagline: 'البشر وراء عجلات القيادة',
      ceo: 'أصحاب الرؤية',
      ceoDesc: 'مؤسسونا لا يزالون لا يصدقون أن الناس يثقون بنا في خطط عطلاتهم. نعدكم أننا محترفون (في معظم الأوقات).',
      drivers: 'سائقونا',
      driversDesc: 'جزء GPS، جزء دليل سياحي، جزء كوميدي. يعرفون كل طريق مختصر، كل مطعم جيد، وكل نكتة سيئة في تركيا.',
      support: 'أبطال دعم العملاء',
      supportDesc: 'التوفر على مدار 24/7 يعني أنهم أجابوا على الأسئلة في الساعة 3 صباحاً بملابس النوم أكثر مما يرغبون في الاعتراف به.',
      mechanics: 'همّاسو السيارات',
      mechanicsDesc: 'يمكنهم سماع مشكلة قبل حدوثها. بجدية، إنه أمر سحري تقريباً. أو ربما يفحصون السيارات كثيراً. على الأرجح ذلك.',
      ourValues: 'ما نؤمن به',
      valuesTagline: 'إلى جانب السيارات النظيفة حقاً',
      value1Title: 'الصدق',
      value1Desc: 'لا رسوم مخفية، لا مفاجآت. باستثناء ربما العثور على ترقية لأننا نحبك.',
      value2Title: 'البهجة',
      value2Desc: 'الحياة قصيرة جداً لتأجير السيارات الممل. نحن نجلب المرح (والمقاعد المريحة).',
      value3Title: 'التميز',
      value3Desc: 'نحن مثاليون. اسأل فريق التفاصيل لدينا الذي يقضي ساعتين لكل سيارة لجعلها تتألق.'
    },
    cars: {
      title: 'أسطولنا المميز',
      subtitle: 'اختر من أسطولنا المميز من المركبات لرحلتك المثالية في تركيا',
      filterCars: 'تصفية السيارات',
      searchCars: 'ابحث عن السيارات...',
      brand: 'الماركة',
      allBrands: 'جميع الماركات',
      transmission: 'ناقل الحركة',
      allTransmissions: 'جميع ناقلات الحركة',
      automatic: 'أوتوماتيك',
      manual: 'يدوي',
      fuelType: 'نوع الوقود',
      allFuelTypes: 'جميع أنواع الوقود',
      petrol: 'بنزين',
      diesel: 'ديزل',
      petrolDiesel: 'بنزين/ديزل',
      hybrid: 'هجين',
      electric: 'كهربائي',
      seats: 'المقاعد',
      anySeats: 'أي مقاعد',
      luggage: 'الأمتعة',
      luggageCapacity: 'حقائب السفر',
      minLicenseAge: 'الحد الأدنى لسن الرخصة',
      years: 'سنوات',
      priceRange: 'نطاق السعر',
      anyPrice: 'أي سعر',
      available: 'متاح',
      bookNow: 'احجز الآن',
      viewDetails: 'عرض التفاصيل',
      perDay: 'في اليوم',
      showing: 'إظهار',
      of: 'من',
      vehicles: 'مركبة',
      noCarsFound: 'لم يتم العثور على سيارات',
      adjustFilters: 'جرب تعديل المرشحات لرؤية المزيد من النتائج',
      features: 'الميزات',
      moreFeatures: 'المزيد',
      year: 'السنة'
    },
    carDetails: {
      backToCars: 'العودة إلى السيارات',
      unavailable: 'غير متاح',
      specifications: 'المواصفات',
      people: 'أشخاص',
      location: 'الموقع',
      brand: 'الماركة',
      features: 'الميزات',
      pricing: 'التسعير',
      dailyRate: 'السعر اليومي',
      weeklyRate: 'السعر الأسبوعي',
      monthlyRate: 'السعر الشهري',
      yearlyRate: 'السعر السنوي',
      contactWhatsApp: 'اتصل عبر واتساب',
      bookNow: 'احجز الآن'
    },
    carFeatures: {
      'manual transmission': 'ناقل حركة يدوي',
      'automatic transmission': 'ناقل حركة أوتوماتيكي',
      'economic': 'اقتصادي',
      'air conditioning': 'تكييف الهواء',
      'bluetooth': 'بلوتوث',
      'gps navigation': 'نظام تحديد المواقع',
      'usb charging': 'شحن USB',
      'backup camera': 'كاميرا خلفية',
      'cruise control': 'التحكم في السرعة',
      'sunroof': 'فتحة سقف',
      'leather seats': 'مقاعد جلدية',
      'heated seats': 'مقاعد مدفأة',
      'premium audio': 'نظام صوتي متميز',
      'digital dashboard': 'لوحة قيادة رقمية',
      'advanced safety': 'السلامة المتقدمة',
      'hybrid engine': 'محرك هجين',
      'all weather': 'لجميع الأحوال الجوية',
      'high seating': 'مقاعد عالية',
      'suv design': 'تصميم SUV',
      'compact design': 'تصميم مدمج',
      'spacious interior': 'مساحة داخلية واسعة',
      'fuel efficient': 'موفر للوقود',
      'modern tech': 'تقنية حديثة'
    },
    footer: {
      description: 'خدمات تأجير سيارات مميزة في تركيا. سائقون محترفون ومركبات فاخرة وخدمة استثنائية لجميع احتياجات النقل.',
      quickLinks: 'روابط سريعة',
      home: 'الرئيسية',
      ourFleet: 'أسطول السيارات',
      services: 'الخدمات',
      campaigns: 'الحملات',
      contact: 'اتصل بنا',
      deliveryForm: 'نموذج التسليم',
      approvedEcommerce: 'ضمان التجارة الإلكترونية المعتمد',
      contactInfo: 'معلومات الاتصال',
      serviceInCities: 'خدمة في جميع المدن الكبرى',
      copyright: '© 2024 Zeyn Tour. جميع الحقوق محفوظة.',
      privacyPolicy: 'سياسة الخصوصية',
      termsOfService: 'شروط الخدمة',
      admin: 'المسؤول',
      poweredBy: 'مدعوم من Prebitit'
    },
    campaigns: {
      title: 'حملات خاصة',
      subtitle: 'اكتشف عروضنا الحصرية والصفقات الخاصة لاحتياجات تأجير السيارات الخاصة بك',
      bookNow: 'احجز الآن',
      backToCampaigns: 'العودة إلى الحملات',
      forRentals: 'لإيجارات 3 أيام أو أكثر',
      startingFrom: 'ابتداءً من'
    },
    dashboard: {
      title: 'لوحة تحكم الإدارة',
      tabs: {
        cars: 'إدارة السيارات',
        bookings: 'إدارة الحجوزات',
        services: 'إدارة الخدمات',
        campaigns: 'إدارة الحملات',
        messages: 'رسائل الاتصال',
        analytics: 'التحليلات'
      },
      loading: 'جاري التحميل',
      manage: {
        addNew: 'إضافة جديد',
        edit: 'تعديل',
        delete: 'حذف',
        cancel: 'إلغاء',
        save: 'حفظ',
        update: 'تحديث',
        create: 'إنشاء',
        actions: 'الإجراءات',
        confirmDelete: 'هل أنت متأكد أنك تريد حذف هذا العنصر؟',
        uploading: 'جاري الرفع...',
        noDataFound: 'لم يتم العثور على بيانات'
      },
      bookingsMgmt: {
        title: 'إدارة الحجوزات',
        addNew: 'إضافة حجز جديد',
        createNew: 'إنشاء حجز جديد',
        customerName: 'اسم العميل',
        car: 'السيارة',
        customerEmail: 'البريد الإلكتروني',
        customerPhone: 'الهاتف',
        pickupLocation: 'مكان الاستلام',
        dropoffLocation: 'مكان التسليم',
        pickupDate: 'تاريخ الاستلام',
        dropoffDate: 'تاريخ التسليم',
        totalPrice: 'السعر الإجمالي',
        status: 'الحالة',
        withDriver: 'مع سائق',
        notes: 'ملاحظات',
        days: 'أيام',
        total: 'الإجمالي',
        driver: 'سائق',
        contact: 'اتصال',
        pickup: 'استلام',
        dropoff: 'تسليم',
        dates: 'التواريخ',
        sameAsPickup: 'نفس مكان الاستلام',
        pending: 'قيد الانتظار',
        confirmed: 'مؤكد',
        cancelled: 'ملغي',
        yes: 'نعم',
        no: 'لا'
      },
      carsMgmt: {
        title: 'إدارة السيارات',
        addNew: 'إضافة سيارة جديدة',
        editCar: 'تعديل السيارة',
        carImages: 'صور السيارة',
        uploadImages: 'انقر لرفع الصور',
        imageFormat: '(JPG, PNG, WEBP حتى 5MB)',
        youtubeLink: 'رابط فيديو يوتيوب (اختياري)',
        brand: 'الماركة',
        model: 'الموديل',
        year: 'السنة',
        seats: 'المقاعد',
        luggage: 'سعة الأمتعة',
        luggageCapacity: 'حقائب السفر',
        minLicenseAge: 'الحد الأدنى لسن الرخصة',
        transmission: 'ناقل الحركة',
        automatic: 'أوتوماتيك',
        manual: 'يدوي',
        fuelType: 'نوع الوقود',
        petrol: 'بنزين',
        diesel: 'ديزل',
        petrolDiesel: 'بنزين/ديزل',
        hybrid: 'هجين',
        electric: 'كهربائي',
        dailyPrice: 'السعر اليومي',
        weeklyPrice: 'السعر الأسبوعي',
        monthlyPrice: 'السعر الشهري',
        yearlyPrice: 'السعر السنوي',
        optional: 'اختياري',
        location: 'الموقع',
        availability: 'التوفر',
        available: 'متاح',
        notAvailable: 'غير متاح',
        image: 'صورة',
        brandModel: 'الماركة والموديل',
        pricePerDay: 'السعر/اليوم',
        actions: 'الإجراءات',
        noImage: 'لا توجد صورة',
        addCar: 'إضافة سيارة',
        updateCar: 'تحديث السيارة',
        vehiclePlate: 'لوحة السيارة',
        vehicleGroup: 'مجموعة السيارة'
      },
      servicesMgmt: {
        title: 'إدارة الخدمات',
        addNew: 'إضافة خدمة جديدة',
        editService: 'تعديل الخدمة',
        nameEn: 'الاسم (الإنجليزية)',
        nameTr: 'الاسم (التركية)',
        nameAr: 'الاسم (العربية)',
        descriptionEn: 'الوصف (الإنجليزية)',
        descriptionTr: 'الوصف (التركية)',
        descriptionAr: 'الوصف (العربية)',
        price: 'السعر',
        priceType: 'نوع السعر',
        perDay: 'يومي',
        perWeek: 'أسبوعي',
        perMonth: 'شهري',
        fixed: 'ثابت',
        active: 'نشط',
        inactive: 'غير نشط',
        name: 'الاسم',
        description: 'الوصف',
        status: 'الحالة',
        image: 'صورة'
      },
      analyticsMgmt: {
        title: 'نظرة عامة على التحليلات',
        totalCars: 'إجمالي السيارات',
        activeCars: 'السيارات النشطة',
        totalBookings: 'إجمالي الحجوزات',
        activeBookings: 'الحجوزات النشطة',
        totalRevenue: 'إجمالي الإيرادات',
        thisMonth: 'هذا الشهر',
        avgBookingValue: 'متوسط قيمة الحجز',
        perBooking: 'لكل حجز',
        fleetUtilization: 'استخدام الأسطول',
        carsInUse: 'السيارات قيد الاستخدام',
        bookingRate: 'معدل الحجز',
        bookingsPerCar: 'الحجوزات لكل سيارة',
        quickInsights: 'رؤى سريعة',
        mostPopularService: 'الخدمة الأكثر شعبية',
        carRentalWithDriver: 'تأجير سيارات مع سائق',
        peakSeason: 'موسم الذروة',
        summerMonths: 'الأشهر الصيفية (يونيو-أغسطس)',
        trending: 'رائج',
        seasonal: 'موسمي'
      },
      campaignsMgmt: {
        title: 'إدارة الحملات',
        addNew: 'إضافة حملة جديدة',
        editCampaign: 'تعديل الحملة',
        titleEn: 'العنوان (الإنجليزية)',
        titleTr: 'العنوان (التركية)',
        titleAr: 'العنوان (العربية)',
        descriptionEn: 'الوصف (الإنجليزية)',
        descriptionTr: 'الوصف (التركية)',
        descriptionAr: 'الوصف (العربية)',
        price: 'السعر',
        priceLabelEn: 'تسمية السعر (الإنجليزية)',
        priceLabelTr: 'تسمية السعر (التركية)',
        priceLabelAr: 'تسمية السعر (العربية)',
        imageUrl: 'رابط الصورة',
        campaignImage: 'صورة الحملة',
        uploadImage: 'انقر لرفع الصورة',
        imageFormat: '(JPG, PNG, WEBP حتى 5MB)',
        active: 'نشط',
        inactive: 'غير نشط',
        displayOrder: 'ترتيب العرض',
        title_column: 'العنوان',
        price_column: 'السعر',
        status: 'الحالة',
        order: 'الترتيب',
        image: 'صورة'
      },
      messagesMgmt: {
        title: 'رسائل الاتصال',
        date: 'التاريخ',
        name: 'الاسم',
        email: 'البريد الإلكتروني',
        phone: 'الهاتف',
        service: 'الخدمة',
        location: 'الموقع',
        message: 'الرسالة',
        status: 'الحالة',
        pending: 'قيد الانتظار',
        read: 'مقروءة',
        noMessages: 'لم يتم العثور على رسائل',
        messageDetails: 'تفاصيل الرسالة',
        markAsRead: 'تعيين كمقروءة',
        close: 'إغلاق',
        view: 'عرض',
        editMessage: 'تعديل الرسالة',
        updateSuccess: 'تم تحديث الرسالة بنجاح',
        deleteSuccess: 'تم حذف الرسالة بنجاح',
        markAsReadSuccess: 'تم تعيينها كمقروءة'
      }
    },
    auth: {
      adminLogin: 'تسجيل دخول المسؤول',
      accessDashboard: 'الوصول إلى لوحة تحكم زين تور',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      rememberMe: 'تذكرني',
      signIn: 'تسجيل الدخول',
      demoCredentials: 'بيانات الدخول التجريبية:',
      loginSuccess: 'تم تسجيل الدخول بنجاح',
      welcomeMessage: 'مرحباً بك في لوحة تحكم زين تور',
      accessDenied: 'تم رفض الوصول. صلاحيات المسؤول مطلوبة.',
      unexpectedError: 'حدث خطأ غير متوقع',
      logout: 'تسجيل الخروج'
    }
  }
};

export const useTranslation = (language: Language) => {
  return {
    t: (key: string) => {
      const keys = key.split('.');
      let value: any = translations[language];
      
      for (const k of keys) {
        value = value?.[k];
      }
      
      return value || key;
    },
    language,
    isRTL: language === 'ar'
  };
};